/**
 * DP REFERENCE LAB — DP101 chapter 5. Why three, and what two cannot do.
 *
 * ── THE CHAPTER'S CLAIM IS A MECHANISM, AND dp-core IMPLEMENTS IT ─────────────────────────────
 * §3: the control system "weights and votes the references, rejecting one that diverges", and —
 * the sentence this lab exists for — "With two references and a disagreement, there is no majority
 * and no way to know which is lying."
 *
 * That is not a maxim. It is a property of `voteReferences`, which computes a median and rejects
 * outliers only when three or more are live; with two, the guard does not run and both are kept.
 * So a learner can make one reference wander, watch the vote identify and discount it, then switch
 * one off and watch the same wander go undetected — the difference between three and two, happening
 * rather than asserted.
 *
 * ── THE DRIFTER IS THE DANGEROUS FAILURE, NOT THE DEAD ONE ────────────────────────────────────
 * A reference that dies is obvious. A reference that slowly walks away is believed, which is why
 * dp-core models the failure as a drift and why the chapter says a DPO watches reference health
 * CONTINUOUSLY rather than periodically.
 *
 * ── TIME IS A SLIDER, NOT A CLOCK ─────────────────────────────────────────────────────────────
 * The measurement functions are deterministic in t, so the same t always gives the same reading and
 * a learner can go back and forth over the moment of divergence. An animation would make this
 * prettier and unrepeatable.
 */
import { useState, useEffect } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import { VOTING_IS, TWO_IS_NOT_ENOUGH, DPO_WATCHES, SOURCE_CH5, WHY_NOT_SCORED, loadCore } from './dplabs.mjs';

const SHIP = { x: 0, y: 0 };
const FAIL_AT = 20;

export default function DpRefLab() {
  const [core, setCore] = useState(null);
  const [t, setT] = useState(45);
  const [drifting, setDrifting] = useState('hpr');
  const [off, setOff] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => { let live = true; loadCore().then((c) => live && setCore(c)).catch((e) => live && setErr(String(e))); return () => { live = false; }; }, []);

  if (err) return <div className="g-glass shell-panel lab"><p className="g-body"><Value of={refused(`The reference engine could not be loaded: ${err}. Nothing is shown.`)} /></p></div>;
  if (!core) return <div className="g-glass shell-panel lab"><p className="g-body g-dim">Loading the reference engine…</p></div>;

  const names = Object.keys(core.REFERENCES);
  const measurements = {};
  for (const n of names) {
    measurements[n] = off.includes(n) ? null
      : core.referenceMeasurement(n, SHIP, t, n === drifting ? { mode: 'drift', failAt: FAIL_AT } : {});
  }
  const vote = core.voteReferences(measurements);
  const live = names.filter((n) => !off.includes(n));
  const caught = vote.rejected && vote.rejected.some((r) => r.name === drifting);

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Reference bench · three opinions, or one truth</div>
      <h2 className="g-h2 lab-h">Turn one off and the drifter stops being detectable.</h2>

      <p className="g-body"><Value of={cited(VOTING_IS, SOURCE_CH5)} /></p>

      <div className="lab-task">
        <div className="g-label">Which reference is wandering, and which are live</div>
        <div className="lab-task-head dpf-picks">
          {names.map((n) => (
            <button key={n} className={`g-btn ${n === drifting ? 'dpf-worst' : ''}`} onClick={() => setDrifting(n)}>{n} drifts</button>
          ))}
        </div>
        <div className="lab-task-head dpf-picks">
          {names.map((n) => (
            <button key={n} className={`g-btn ${off.includes(n) ? 'dpb-off' : 'dpb-on'}`}
              onClick={() => setOff((o) => (o.includes(n) ? o.filter((x) => x !== n) : [...o, n]))}>
              {n} {off.includes(n) ? 'off' : 'on'}
            </button>
          ))}
        </div>
        <label className="dpr-time">
          <span className="g-dim">time {t}s — the drift begins at {FAIL_AT}s</span>
          <input type="range" min="0" max="120" value={t} onChange={(e) => setT(Number(e.target.value))} />
        </label>
      </div>

      {/* ── WHAT EACH ONE SAYS, AND WHAT THE VOTE DID WITH IT ── */}
      <div className="lab-task">
        <div className="g-label">What each reference is reporting at this instant</div>
        <table className="dpb-table">
          <tbody>
            {names.map((n) => {
              const m = measurements[n];
              const rej = vote.rejected && vote.rejected.find((r) => r.name === n);
              return (
                <tr key={n} className={rej ? 'dpb-broke' : ''}>
                  <td><b>{n}</b> <span className="g-dim">{core.REFERENCES[n].noise_m} m noise</span></td>
                  <td className="dpb-num">
                    {m ? <Value of={computed(`${m.x.toFixed(2)}, ${m.y.toFixed(2)} m`)} /> : <Value of={absent()} />}
                  </td>
                  <td>
                    {!m ? <span className="g-dim">switched off</span>
                      : rej ? <Value of={refused(`rejected — ${rej.off_m} m from the median`)} />
                      : <Value of={computed('used')} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── THE VERDICT — and the chapter's sentence, made to happen ── */}
      <div className="lab-task dpb-verdict">
        <div className="g-label">What the system believes, and whether it caught the liar</div>
        {!vote.ok ? (
          <p className="g-body"><Value of={refused(vote.reason)} /></p>
        ) : (
          <p className="g-body">
            Position in use: <Value of={computed(`${vote.x.toFixed(2)}, ${vote.y.toFixed(2)} m`)} />{' '}
            <span className="g-dim">from {vote.count} reference(s): {vote.used.join(', ')}</span>
          </p>
        )}
        {live.length >= 3 ? (
          <p className="g-body">
            {caught
              ? <Value of={computed(`Three live: the median identifies ${drifting} and discounts it.`)} />
              : <Value of={computed('Three live: the vote can reject a diverging reference once it diverges far enough.')} />}
          </p>
        ) : live.length === 2 ? (
          <>
            <p className="g-body"><Value of={refused('Two live: no rejection is possible. Both are kept and the drifter is believed.')} /></p>
            <p className="g-body"><Value of={cited(TWO_IS_NOT_ENOUGH, SOURCE_CH5)} /></p>
          </>
        ) : (
          <p className="g-body"><Value of={refused('Fewer than two: she is losing her knowledge of where she is. That is an ASOG escalation, not a note in the log.')} /></p>
        )}
        <p className="g-body g-dim">{core.REFERENCES[drifting].basis}</p>
      </div>

      <p className="g-body g-dim"><Value of={cited(DPO_WATCHES, SOURCE_CH5)} /> Not “checks periodically”.</p>
      <p className="g-body g-dim lab-foot">{WHY_NOT_SCORED}</p>
      <p className="g-body g-dim"><Value of={absent()} /> Nothing here is an attestation.</p>
    </div>
  );
}
